import React from 'react';
import { Box, Title, Text, Group, Divider, List, Container } from '@mantine/core';
export function Methodology() {
  return <Box className="p-8 bg-white rounded-lg shadow-sm" data-id="element-206">
      <Container size="lg" data-id="element-207">
        <Title order={2} className="mb-4 text-slate-800" data-id="element-208">
          Methodology Overview
        </Title>
        <Text className="text-slate-600 mb-8" data-id="element-209">
          Welcome to Nutrivector — a computational framework for understanding
          nutrition and lifestyle design through a systems lens. This page
          explains the philosophy, construction, and application of
          Nutrivector's scoring and stacking system.
        </Text>
        <Divider className="my-8" data-id="element-210" />
        {/* Nutrivector Approach Section */}
        <div className="mb-12 bg-slate-50 p-6 rounded-lg" data-id="element-211">
          <Title order={3} className="text-slate-800 mb-4" data-id="element-212">
            Nutrivector Approach
          </Title>
          <Text className="text-slate-700 mb-4" data-id="element-213">
            Most people approach nutrition in 1, 2, or maybe 3 dimensions:
          </Text>
          <List className="text-slate-700 ml-4 pl-4 mb-4" data-id="element-214">
            <List.Item data-id="element-215">What to eat (e.g., carnivore vs. vegan)</List.Item>
            <List.Item data-id="element-216">When to eat (e.g., intermittent fasting)</List.Item>
            <List.Item data-id="element-217">How much to eat (e.g., macros or calories)</List.Item>
          </List>
          <Text className="text-slate-700 mb-4" data-id="element-218">
            But that misses something important. In reality, nutrition is a
            multidimensional system, and optimization occurs only when all major
            dimensions are accounted for:
          </Text>
          <List className="text-slate-700 ml-4 pl-4 mb-4" data-id="element-219">
            <List.Item data-id="element-220">Food type</List.Item>
            <List.Item data-id="element-221">Timing</List.Item>
            <List.Item data-id="element-222">Frequency</List.Item>
            <List.Item data-id="element-223">Macros</List.Item>
            <List.Item data-id="element-224">Micronutrients</List.Item>
            <List.Item data-id="element-225">Exercise integration</List.Item>
            <List.Item data-id="element-226">Medical/cultural constraints</List.Item>
          </List>
          <Text className="text-slate-700" data-id="element-227">
            Nutrivector is built to solve that. It offers a full-spectrum
            framework that reveals how every protocol stacks across 12 outcome
            domains, from athletic performance to satiety, from hormonal balance
            to cultural fit.
          </Text>
        </div>
        {/* What Nutrivector Is Not Section */}
        <div className="mb-12" data-id="element-228">
          <Title order={3} className="text-slate-800 mb-4" data-id="element-229">
            What Nutrivector Is Not
          </Title>
          <List className="text-slate-700 ml-4 pl-4" data-id="element-230">
            <List.Item className="mb-2" data-id="element-231">
              It is not a meal plan generator
            </List.Item>
            <List.Item className="mb-2" data-id="element-232">
              It is not a single prescriptive diet
            </List.Item>
            <List.Item className="mb-2" data-id="element-233">
              It is not locked to one ideology
            </List.Item>
            <List.Item data-id="element-234">
              It is not anti-mainstream or anti-expert — it integrates both
            </List.Item>
          </List>
        </div>
        <Divider className="my-8" data-id="element-235" />
        {/* How to Use Nutrivector Section */}
        <div className="mb-12 bg-slate-50 p-6 rounded-lg" data-id="element-236">
          <Title order={3} className="text-slate-800 mb-4" data-id="element-237">
            How to Use Nutrivector
          </Title>
          <List className="text-slate-700 ml-4 pl-4" data-id="element-238">
            <List.Item className="mb-2" data-id="element-239">
              Explore each layer in the app and learn how the protocols
              function.
            </List.Item>
            <List.Item className="mb-2" data-id="element-240">
              View radar ratings to understand the tradeoffs across 12 health
              domains.
            </List.Item>
            <List.Item className="mb-2" data-id="element-241">
              Filter by what matters to you — for example, if you're optimizing
              for cognition, ease, and digestion, compare protocols that score
              positively in those domains.
            </List.Item>
            <List.Item className="mb-2" data-id="element-242">
              Stack compatible protocols from each layer to form a personalized
              vector: Example: Kosher Daystride Flux 3/4 Spice FED
            </List.Item>
            <List.Item data-id="element-243">Track your own response and adjust.</List.Item>
          </List>
        </div>
        {/* Data Integrity Section */}
        <div className="mb-12" data-id="element-244">
          <Title order={3} className="text-slate-800 mb-4" data-id="element-245">
            Data Integrity & Scoring Philosophy
          </Title>
          <Text className="text-slate-700 mb-4" data-id="element-246">
            All scoring is grounded in:
          </Text>
          <List className="text-slate-700 ml-4 pl-4 mb-4" data-id="element-247">
            <List.Item className="mb-2" data-id="element-248">
              Scientific literature from reputable sources (Healthline, PubMed,
              Bodybuilding.com)
            </List.Item>
            <List.Item className="mb-2" data-id="element-249">
              Clinical use cases and cultural patterns (e.g., Blue Zones,
              athletic regimens)
            </List.Item>
            <List.Item data-id="element-250">
              Functional alignment across the 12 radar domains
            </List.Item>
          </List>
          <Text className="text-slate-700" data-id="element-251">
            Scoring is relative, not absolute. Oftentimes there is friction
            within a domain's optimization, often due to tradeoffs (e.g., OMAD
            may boost longevity but reduce athletic performance).
          </Text>
        </div>
        <Divider className="my-8" data-id="element-252" />
        {/* What's Next Section */}
        <div className="bg-slate-50 p-6 rounded-lg" data-id="element-253">
          <Title order={3} className="text-slate-800 mb-4" data-id="element-254">
            What's Next
          </Title>
          <Text className="text-slate-700" data-id="element-255">
            As you explore, remember: Nutrivector is about awareness. I hope
            this tool helps you as you grow in your health and wellness. Explore
            and enjoy.
          </Text>
        </div>
      </Container>
    </Box>;
}